import React from 'react';
import Tooltips from '../includes/Tooltips';

export default function BuildBroadband(props) {
  const [broadbandResult, setBroadbandResult] = React.useState([]);
  const [connection, setConnection] = React.useState("");

  // Prices start empty – populated from real data
  const [liteFibrePrice, setLiteFibrePrice] = React.useState("");
  const [everydayFibrePrice, setEverydayFibrePrice] = React.useState("");
  const [fibreProPrice, setFibreProPrice] = React.useState("");
  const [starterPrice, setStarterPrice] = React.useState("");

  React.useEffect(() => {
    setConnection(props.values.connectionOption || "");
  }, [props.values.connectionOption]);

  // Update prices & preserve current selection when connection option changes
  React.useEffect(() => {
    if (
      !props.values.connectionOption ||
      !Array.isArray(props.values.broadbandResult) ||
      props.values.broadbandResult.length === 0
    ) {
      setLiteFibrePrice("");
      setEverydayFibrePrice("");
      setFibreProPrice("");
      setStarterPrice("");
      if (props.setBBPlan) props.setBBPlan([]);
      return;
    }

    const isByo = props.values.connectionOption === 'BYO Modem';

    // Update individual price states for display
    props.values.broadbandResult.forEach((plan) => {
      const price = isByo ? (plan.StandardRate || plan.Price || "") : (plan.Price || "");

      switch (plan.Title) {
        case "Everyday Fibre":
          setEverydayFibrePrice(price);
          break;
        case "Lite Fibre*":
          setLiteFibrePrice(price);
          break;
        case "Fibre Pro":
          setFibreProPrice(price);
          break;
        case "Starter ADSL":
          setStarterPrice(price);
          break;
        default:
          break;
      }
    });

    // If a plan is already selected → only update its price, don't overwrite selection
    if (props.defaultPlan && props.setBBPlan) {
      const selectedTitle = props.defaultPlan;
      const selectedPlan = props.values.broadbandResult.find(p => p.Title === selectedTitle);

      if (selectedPlan) {
        const newPrice = isByo ? (selectedPlan.StandardRate || selectedPlan.Price) : selectedPlan.Price;

        props.setBBPlan([{
          plan: selectedTitle,
          connection: selectedPlan.Connection,
          price: newPrice,
        }]);
      }
    }
  }, [props.values.connectionOption, props.values.broadbandResult, props.defaultPlan, props.setBBPlan]);

  // Sync broadbandResult from props
  React.useEffect(() => {
    if (Array.isArray(props.values.broadbandResult) && props.values.broadbandResult.length > 0) {
      setBroadbandResult(props.values.broadbandResult);
    }
  }, [props.values.broadbandResult]);

  const handleOptionChange = (e, planTitle, connection, price) => {
    if (props.setBBPlan) {
      props.setBBPlan([{
        plan: planTitle,
        connection,
        price,
      }]);
    }

    if (typeof props.setDefaultPlan === 'function') {
      props.setDefaultPlan(planTitle);
    }

    // Optional: reset connection option after selection
    // if (props.setConnectionOption) props.setConnectionOption('');

    if (props.toggleCheck) {
      props.toggleCheck('broadband');
    }
  };

  const removeOptionChange = () => {
    if (props.setBBPlan) {
      props.setBBPlan([{
        title: "",
        connection: "",
        price: "",
        icon: "",
      }]);
    }

    if (typeof props.setDefaultPlan === 'function') {
      props.setDefaultPlan("");
    }

    if (props.setPhonePlan) {
      props.setPhonePlan([]);
    }

    if (props.toggleCheck) {
      props.toggleCheck('broadband');
    }
  };

  const handleConnectionOptionChange = (title) => {
    if (props.setConnectionOption) {
      props.setConnectionOption(title);
    }
    setConnection(title);
  };

  const removeConnectionOptionChange = () => {
    if (props.setConnectionOption) {
      props.setConnectionOption("");
    }
    setConnection("");
  };

  const displayConnectionOptions = (planTitle) => {
    const connectionSummary = props.values.connectionSummary || [];
    const planTitleStrip = planTitle.replace(/\s+/g, '').toLowerCase().replace(/[^\w]/g, '');

    return (
      <div className="connection-items">
        <h3>Select Modem Options</h3>
        {connectionSummary.map((object, i) => {
          const titleString = object.Title.replace(/\s+/g, '').toLowerCase();
          const isSelected = connection === object.Title;

          return (
            <div className="connection-item" key={i}>
              <div className="connection-item-detail">
                <div className="connection-item-detail--title">
                  <span className="connectiontitle" dangerouslySetInnerHTML={{ __html: object.Title }} />
                  {object.Recommended === 1 && <span className="connectionrecommended">Recommended</span>}
                </div>
                <div dangerouslySetInnerHTML={{ __html: object.Details }} />
              </div>
              <div className="connection-item-price-group">
                {isSelected ? (
                  <span className="connection-item-link selected" onClick={removeConnectionOptionChange}>
                    Selected
                  </span>
                ) : (
                  <span
                    className={`connection-item-link connection-item-link-${planTitleStrip}-${titleString}${i}`}
                    onClick={() => handleConnectionOptionChange(object.Title)}
                  >
                    Choose Option
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const CheckedIcon = () => (
    <svg
      onClick={removeOptionChange}
      className="broadband-item-checked"
      width="57"
      height="56"
      viewBox="0 0 57 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="28.5" cy="28" r="28" fill="#034e52" />
      <path
        d="M39.591 21.25L24.341 36.5L17.4092 29.5682"
        stroke="white"
        strokeWidth="2.77273"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const displayBroadbandItems = () => {
    if (!Array.isArray(broadbandResult) || broadbandResult.length === 0) {
      return null;
    }

    const sortedPlans = [...broadbandResult].sort((a, b) => (a.SortOrder || 999) - (b.SortOrder || 999));

    return sortedPlans.map((object, i) => {
      const isSelected = props.defaultPlan === object.Title;

      let displayPrice = object.Price || "—";
      if (object.Title === 'Lite Fibre*' && liteFibrePrice) displayPrice = liteFibrePrice;
      else if (object.Title === 'Everyday Fibre' && everydayFibrePrice) displayPrice = everydayFibrePrice;
      else if (object.Title === 'Fibre Pro' && fibreProPrice) displayPrice = fibreProPrice;
      else if (object.Title === 'Starter ADSL' && starterPrice) displayPrice = starterPrice;

      return (
        <li key={i} className={isSelected ? "toggled-broadband-item" : ""}>
          <div className={isSelected ? "toggled broadband-item" : "broadband-item"}>
            <div className="broadband-item-detail">
              <div className="broadband-item-detail--title">
                {object.Title} <Tooltips values={{ userClassTooltips: `<div class='broadband-tooltip'>${object.Details}</div>` }} placement="right" />
              </div>
              <div dangerouslySetInnerHTML={{__html: object.Details}}></div>
            </div>
            <div className="broadband-item-price-group">
              <span className="broadband-item-price">
                <span>{displayPrice}<span className="broadband-item-price--small">/month</span></span>
              </span>
              {isSelected ? (
                CheckedIcon()
              ) : (
                <span
                  className="broadband-item-link"
                  onClick={(e) => handleOptionChange(e, object.Title, object.Connection, displayPrice)}
                >
                  Choose Plan
                </span>
              )}
            </div>
          </div>
          {isSelected && displayConnectionOptions(object.Title)}
        </li>
      );
    });
  };

  return (
    <React.Fragment>
      <ul className="broadband-items">
        {displayBroadbandItems()}
      </ul>
    </React.Fragment>
  );
}